import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class MtoProductOffer extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.mto-offer');
    if (!this.section) return;

    this.mode = this.dataset.mode || 'single';

    this.totalEl = this.querySelector('[data-mto-total]');
    this.items = Array.from(this.section.querySelectorAll('.mto-offer__item'));

    this.addSingleHandler = this.#handleAddSingle.bind(this);
    this.addSelectedHandler = this.#handleAddSelected.bind(this);
    this.toggleHandler = this.#handleToggleChange.bind(this);

    this.section.addEventListener('click', this.addSingleHandler);
    this.section.addEventListener('click', this.addSelectedHandler);

    if (this.mode === 'multi') {
      this.section.addEventListener('change', this.toggleHandler);
      this.#updateTotal();
    }
  }

  disconnectedCallback() {
    if (!this.section) return;
    this.section.removeEventListener('click', this.addSingleHandler);
    this.section.removeEventListener('click', this.addSelectedHandler);
    this.section.removeEventListener('change', this.toggleHandler);
  }

  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  #moneyFromCents(cents) {
    // We rely on Shopify-rendered money in Liquid for display.
    // For total display, we update text with a simple formatter fallback:
    // If your store needs exact formatting, we can pass a money format string later.
    const value = Math.max(0, Number(cents) || 0) / 100;
    return value.toLocaleString(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' });
  }

  #getItemFinalPriceCents(item) {
    const v = item?.dataset?.finalPrice;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  #isItemSelected(item) {
    const toggle = item.querySelector('.mto-offer__toggle-input');
    return !!toggle?.checked;
  }

  #updateTotal() {
    if (!this.totalEl) return;

    let total = 0;
    for (const item of this.items) {
      if (this.#isItemSelected(item)) total += this.#getItemFinalPriceCents(item);
    }

    // Update total
    this.totalEl.textContent = this.#moneyFromCents(total);
  }

  #handleToggleChange(event) {
    if (this.mode !== 'multi') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.classList.contains('mto-offer__toggle-input')) return;

    this.#updateTotal();
  }

  async #handleAddSingle(event) {
    const btn = event.target?.closest?.('[data-mto-add-single]');
    if (!btn) return;

    // In multi mode we should never show these buttons, but guard anyway
    if (this.mode === 'multi') return;

    const item = btn.closest('.mto-offer__item');
    if (!item) return;

    const variantId = item.dataset.variantId;
    if (!variantId) return;

    await this.#addToCart([{ id: variantId, quantity: 1 }], btn);
  }

  async #handleAddSelected(event) {
    const btn = event.target?.closest?.('[data-mto-add-selected]');
    if (!btn) return;

    if (this.mode !== 'multi') return;

    const selected = this.items
      .filter((item) => this.#isItemSelected(item))
      .map((item) => ({ id: item.dataset.variantId, quantity: 1 }))
      .filter((x) => x.id);

    if (!selected.length) return;

    await this.#addToCart(selected, btn);
  }

  async #addToCart(items, sourceButton) {
    const sectionIds = this.#getCartSectionIds();

    const formData = new FormData();
    items.forEach((it, idx) => {
      formData.append(`items[${idx}][id]`, it.id);
      formData.append(`items[${idx}][quantity]`, String(it.quantity || 1));
    });

    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });

      const json = await res.json();

      if (json?.status) {
        // Shopify error shape: {status, message, description}
        console.error('MTO offer add error:', json);
        return;
      }

      // Dispatch a Horizon-style cart add event so cart drawer/components update
      document.dispatchEvent(
        new CartAddEvent({}, String(items[0]?.id || ''), {
          source: 'mto-product-offer',
          itemCount: items.reduce((sum, it) => sum + (Number(it.quantity) || 1), 0),
          sections: json.sections,
        })
      );

      // Optional: update total again (if needed)
      if (this.mode === 'multi') this.#updateTotal();

      // Let Horizon button animation finish (handled by add-to-cart-component)
      if (sourceButton instanceof HTMLElement) {
        // No-op; animation already triggered by on:click="/handleClick"
      }
    } catch (e) {
      console.error('MTO offer add failed:', e);
    }
  }
}

if (!customElements.get('mto-product-offer')) {
  customElements.define('mto-product-offer', MtoProductOffer);
}
